import React, { useEffect, useRef, useState, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faSync,
	faPlus,
	faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import { tableStyles } from "../../styles/tableStyles"
import { selectTarif } from "../../../hooks/useTarif"
import { useRights } from "../../../hooks/rights"
import Loader from "../../basics/Loader"
import CustomModal from "../../basics/interface/CustomModal"
import { URL } from "../../../store/actions/depannageActions"
import { batterieCheckPDF } from "../../PDF/BatterieCheckPDF"
import { MONTHS, YEARS } from "../../../utilities/Addons"
import useInventaireUrlState from "./useInventaireUrlState"
import CommandeCard from "./CommandeCard"
import CommandeVirtuelleCard from "./CommandeVirtuelleCard"
import FournisseursV2 from "./FournisseursV2"
import OptionsV2 from "./OptionsV2"
import UtilisationsV2 from "./UtilisationsV2"
import CommandeModalV2 from "./CommandeModalV2"
import StockVirtuelCard from "./StockVirtuelCard"
import ConsoModalV2 from "./ConsoModalV2"
import CmdPersoModalV2 from "./CmdPersoModalV2"
import TransfertModalV2 from "./TransfertModalV2"
import CorrectionModalV2 from "./CorrectionModalV2"
import {
	fetchInventaireCategories,
	fetchInventaireStock,
	fetchInventaireLimitChecker,
	fetchInventaireFournisseurs,
	fetchInventaireAllArticles,
	fetchInventaireCommandes,
	postInventaireCommande,
	postInventaireCommandeReset,
	fetchInventaireVirtuelCommandes,
	fetchStockVirtuel,
	fetchInventaireConsommables,
	postInventaireConsommation,
	postInventaireConsommationReset,
	fetchLiensVirtuels,
	postInventaireTransfert,
	postInventaireTransfertReset,
	postInventaireCorrection,
	postInventaireCorrectionReset,
	fetchStockNonDebiteCount,
} from "../../../store/actions/atelierInventaireActions"

let savedScrollY = 0

const kwargsMatch = (stored, current) => {
	if (!stored) return false
	const sk = Object.keys(stored).filter(
		(k) => k !== "offset" && k !== "limit",
	)
	const ck = Object.keys(current).filter(
		(k) => k !== "offset" && k !== "limit",
	)
	if (sk.length !== ck.length) return false
	return sk.every((k) => String(stored[k]) === String(current[k]))
}

const TABS = [
	{ key: "stock", label: "Stock", right: "inventaire" },
	{ key: "commandes", label: "Commandes", right: "inventaire" },
	{
		key: "stockVirtuel",
		label: "Stock Virtuel",
		right: "inventaire_virtuel",
	},
	{
		key: "commandesVirtuelles",
		label: "Commandes Virtuelles",
		right: "inventaire_virtuel",
	},
	{ key: "fournisseurs", label: "Fournisseurs", right: "inventaire" },
	{ key: "options", label: "Options", right: "inventaire" },
	// GE-55 : responsable_atelier + admin_atelier (boss par bypass).
	{
		key: "utilisations",
		label: "Utilisations",
		right: "inventaire_utilisations",
	},
]

// Taille de page de l'endless scroll (commandes / commandes virtuelles / stock
// virtuel).
const CMD_LIMIT = 10
const CV_LIMIT = 10
const SV_LIMIT = 10

// Noms de mois du fichier Excel : minuscules SANS accent (décision Jordan
// 12.08.2026) — MONTHS (accentué) reste celui de l'UI.
const MONTHS_FILE = [
	"-",
	"janvier",
	"fevrier",
	"mars",
	"avril",
	"mai",
	"juin",
	"juillet",
	"aout",
	"septembre",
	"octobre",
	"novembre",
	"decembre",
]

// La sentinelle de bas de liste est-elle dans la zone de préchargement (200px
// sous le viewport) ? Vérif synchrone via getBoundingClientRect — sert à
// enchaîner les pages de façon fiable après un chargement, sans dépendre de la
// notification asynchrone de l'IntersectionObserver (parfois perdue lors des
// recréations rapides → le chargement ne se déclenchait pas en bas de page).
const SCROLL_MARGIN = 200
const isSentinelInView = (el) => {
	if (!el) return false
	const vh = window.innerHeight || document.documentElement.clientHeight
	const r = el.getBoundingClientRect()
	return r.top <= vh + SCROLL_MARGIN && r.bottom >= -SCROLL_MARGIN
}

// Bouton « Corriger » d'une ligne de stock (chef_atelier) — seul chemin
// d'édition manuelle de la quantité : correction typée + motivée sur les lots
// existants, tracée et notifiée (CorrectionModalV2).
const corrigerBtn = {
	padding: "4px 12px",
	borderRadius: 8,
	border: `1px solid ${COLORS.border}`,
	backgroundColor: COLORS.surface,
	color: COLORS.textMuted,
	fontSize: 12,
	fontWeight: 600,
	cursor: "pointer",
	fontFamily: "inherit",
}

// Ligne du tableau de stock (lecture seule : les quantités ne bougent que via
// commandes/débits/corrections tracées pour garder lots et prix d'achat
// cohérents). Surlignage rouge si l'article est sous le seuil (low).
const StockRow = ({ article, index, low, onCorriger }) => {
	const [hover, setHover] = useState(false)
	const bg = hover ? tableStyles.rowHover : tableStyles.rowBg(index)
	const aCommander = article.a_commander || 0
	const firstTd = low
		? {
				...tableStyles.td,
				borderLeft: `3px solid ${COLORS.danger}`,
				paddingLeft: 13,
			}
		: tableStyles.td
	return (
		<tr
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				backgroundColor: bg,
				transition: "background-color 0.12s",
			}}
		>
			<td style={firstTd}>
				{aCommander > 0 && (
					<FontAwesomeIcon
						icon={faExclamationTriangle}
						title="À commander (besoin des fiches dès « Devis accepté »)"
						style={{
							color: COLORS.warning,
							fontSize: 12,
							marginRight: 6,
						}}
					/>
				)}
				{article.categorieNom}
			</td>
			<td style={{ ...tableStyles.td, fontWeight: 500 }}>
				{article.nom}
				{aCommander > 0 && (
					<span
						style={{
							marginLeft: 8,
							fontSize: 11,
							fontWeight: 400,
							color: COLORS.warning,
						}}
					>
						à commander : {aCommander}
					</span>
				)}
			</td>
			<td style={tableStyles.td}>{article.prixVente}</td>
			<td style={tableStyles.td}>{article.limite}</td>
			<td
				style={{
					...tableStyles.td,
					color: low ? COLORS.danger : COLORS.text,
					fontWeight: low ? 600 : 400,
				}}
			>
				{article.quantite_reservee > 0
					? `${article.quantite} (${article.quantite_reservee})`
					: article.quantite}
			</td>
			{onCorriger && (
				<td style={{ ...tableStyles.td, textAlign: "right" }}>
					<button
						type="button"
						style={corrigerBtn}
						onClick={() => onCorriger(article)}
					>
						Corriger
					</button>
				</td>
			)}
		</tr>
	)
}

const InventaireV2 = () => {
	const dispatch = useDispatch()
	const atelierInventaire = useSelector((s) => s.atelierInventaire)
	const groups = useSelector((s) => s.navInfo.login.groups)
	const token = useSelector((s) => s.navInfo.login.token)

	const { hasRight } = useRights()
	const isChef = groups.includes("responsable_atelier")
	const isBoss = groups.includes("boss")

	const tva = useSelector((s) => selectTarif(s).tva)

	const visibleTabKeys = TABS.filter(
		(t) => (!t.right || hasRight(t.right)) && (!t.boss || isBoss),
	).map((t) => t.key)

	const { initial: urlState, syncUrl } = useInventaireUrlState(visibleTabKeys)

	const [tab, setTab] = useState(urlState.tab)
	const [categorieSelected, setCategorieSelected] = useState(
		urlState.stock.categorie,
	)
	const [lowOnly, setLowOnly] = useState(urlState.stock.lowOnly)
	const [aCommanderOnly, setACommanderOnly] = useState(
		urlState.stock.aCommanderOnly,
	)

	const [cmdFournisseur, setCmdFournisseur] = useState(
		urlState.commandes.fournisseur,
	)
	const [cmdArticle, setCmdArticle] = useState(urlState.commandes.article)
	const [cmdModalOpen, setCmdModalOpen] = useState(false)
	// Sentinelles endless scroll : nœud DOM en state (callback ref) plutôt que
	// useRef — la liste est démontée pendant le spinner principal (filtre,
	// refetch) puis remontée ; un ref figé laissait l'IntersectionObserver
	// accroché à l'ancien nœud si la longueur/hasMore n'avaient pas bougé (10→10)
	// → scroll infini mort une fois sur deux.
	const [cmdSentinel, setCmdSentinel] = useState(null)

	const [cvFournisseur, setCvFournisseur] = useState(
		urlState.commandesVirtuelles.fournisseur,
	)
	// GE-79 : filtre « Annulées » (commande annulée ou au moins une ligne).
	const [cvAnnulees, setCvAnnulees] = useState(
		urlState.commandesVirtuelles.annulees,
	)
	const [cvSentinel, setCvSentinel] = useState(null)

	const employes = useSelector((s) => s.masterReducer.employes)
	const [svFournisseur, setSvFournisseur] = useState(
		urlState.stockVirtuel.fournisseur,
	)
	const [svMonth, setSvMonth] = useState(urlState.stockVirtuel.mois)
	const [svYear, setSvYear] = useState(urlState.stockVirtuel.annee)
	const [consoTarget, setConsoTarget] = useState(null) // { ci, ai }
	const [cmdPersoId, setCmdPersoId] = useState(null)
	// Modal de transfert vers le stock physique (chef) :
	// { commandeId, initial } — initial = quantités pré-remplies par id
	// d'article virtuel (raccourci depuis ConsoModalV2), null = tout au dispo.
	const [transfertTarget, setTransfertTarget] = useState(null)
	const [svSentinel, setSvSentinel] = useState(null)

	// Article ciblé par le modal de correction du stock physique (chef).
	const [correctionTarget, setCorrectionTarget] = useState(null)

	const { categories, fetchCategories } = atelierInventaire
	const { stock, fetchStock, stockNonDebite } = atelierInventaire
	const { limitChecker } = atelierInventaire
	const { fournisseurs, allArticles } = atelierInventaire
	const {
		commandes,
		fetchCommandes,
		postCommande,
		commandesHasMore,
		commandesLoadingMore,
	} = atelierInventaire
	const {
		commandesVirtuelles,
		fetchCommandesVirtuelles,
		commandesVirtuellesHasMore,
		commandesVirtuellesLoadingMore,
	} = atelierInventaire
	const {
		stockVirtuel,
		consommables,
		postConsommation,
		stockVirtuelHasMore,
		stockVirtuelLoadingMore,
		liensVirtuels,
		postTransfert,
		postCorrection,
	} = atelierInventaire
	// NB: ne pas déstructurer `fetchStockVirtuel` (collision avec l'action).
	const svLoading = atelierInventaire.fetchStockVirtuel

	const mountRef = useRef(true)

	useEffect(() => {
		dispatch(fetchInventaireCategories())
		if (urlState.stock.categorie > 0) {
			dispatch(fetchInventaireStock(urlState.stock.categorie))
		} else {
			dispatch(fetchInventaireStock())
		}
		dispatch(fetchInventaireLimitChecker())
		dispatch(fetchStockNonDebiteCount())
		dispatch(fetchInventaireFournisseurs())
		dispatch(fetchInventaireAllArticles())
	}, [dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		return () => {
			savedScrollY = window.scrollY
		}
	}, [])

	useEffect(() => {
		if (mountRef.current && savedScrollY > 0) {
			requestAnimationFrame(() => window.scrollTo(0, savedScrollY))
			savedScrollY = 0
		}
	}, [])

	useEffect(() => {
		if (tab !== "commandes") return
		const kwargs = { offset: 0, limit: CMD_LIMIT }
		if (cmdFournisseur > 0) kwargs.fournisseur = cmdFournisseur
		if (cmdArticle > 0) kwargs.article = cmdArticle
		if (
			mountRef.current &&
			commandes.length > 0 &&
			kwargsMatch(atelierInventaire.lastCommandesKwargs, kwargs)
		)
			return
		dispatch(fetchInventaireCommandes(kwargs))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab])

	// Endless scroll des commandes.
	useEffect(() => {
		if (tab !== "commandes") return
		const el = cmdSentinel
		if (!el || fetchCommandes.loading) return
		const loadNext = () => {
			if (!commandesHasMore || commandesLoadingMore) return
			const kwargs = { offset: commandes.length, limit: CMD_LIMIT }
			if (cmdFournisseur > 0) kwargs.fournisseur = cmdFournisseur
			if (cmdArticle > 0) kwargs.article = cmdArticle
			dispatch(fetchInventaireCommandes(kwargs))
		}
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) loadNext()
			},
			{ rootMargin: `${SCROLL_MARGIN}px` },
		)
		obs.observe(el)
		// Auto-continue fiable : sentinelle encore visible après un chargement.
		if (isSentinelInView(el)) loadNext()
		return () => obs.disconnect()
	}, [
		tab,
		dispatch,
		cmdSentinel,
		fetchCommandes.loading,
		commandes.length,
		commandesHasMore,
		commandesLoadingMore,
		cmdFournisseur,
		cmdArticle,
	])

	// Après création d'une commande : refetch + fermeture différée.
	useEffect(() => {
		if (!postCommande.saved) return
		const t = setTimeout(() => {
			dispatch(postInventaireCommandeReset())
			setCmdModalOpen(false)
			const kwargs = { offset: 0, limit: CMD_LIMIT }
			if (cmdFournisseur > 0) kwargs.fournisseur = cmdFournisseur
			if (cmdArticle > 0) kwargs.article = cmdArticle
			dispatch(fetchInventaireCommandes(kwargs))
		}, 800)
		return () => clearTimeout(t)
	}, [postCommande.saved, dispatch, cmdFournisseur, cmdArticle])

	useEffect(() => {
		if (tab !== "commandesVirtuelles") return
		const kwargs = { offset: 0, limit: CV_LIMIT }
		if (cvFournisseur > 0) kwargs.id = cvFournisseur
		if (cvAnnulees) kwargs.annulees = 1
		if (
			mountRef.current &&
			commandesVirtuelles.length > 0 &&
			kwargsMatch(atelierInventaire.lastCommandesVirtuellesKwargs, kwargs)
		)
			return
		dispatch(fetchInventaireVirtuelCommandes(kwargs))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab])

	// Endless scroll : on observe une sentinelle en bas de liste ; quand elle
	// entre dans le viewport et qu'il reste des pages, on charge la suivante.
	// L'observer est recréé à chaque changement d'état pour capturer l'offset
	// courant.
	useEffect(() => {
		if (tab !== "commandesVirtuelles") return
		const el = cvSentinel
		if (!el || fetchCommandesVirtuelles.loading) return
		const loadNext = () => {
			if (!commandesVirtuellesHasMore || commandesVirtuellesLoadingMore)
				return
			const kwargs = {
				offset: commandesVirtuelles.length,
				limit: CV_LIMIT,
			}
			if (cvFournisseur > 0) kwargs.id = cvFournisseur
			if (cvAnnulees) kwargs.annulees = 1
			dispatch(fetchInventaireVirtuelCommandes(kwargs))
		}
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) loadNext()
			},
			{ rootMargin: `${SCROLL_MARGIN}px` },
		)
		obs.observe(el)
		// Auto-continue fiable : sentinelle encore visible après un chargement.
		if (isSentinelInView(el)) loadNext()
		return () => obs.disconnect()
	}, [
		tab,
		dispatch,
		cvSentinel,
		fetchCommandesVirtuelles.loading,
		commandesVirtuelles.length,
		commandesVirtuellesHasMore,
		commandesVirtuellesLoadingMore,
		cvFournisseur,
		cvAnnulees,
	])

	useEffect(() => {
		if (tab !== "stockVirtuel") return
		const kwargs = { offset: 0, limit: SV_LIMIT }
		if (svFournisseur > 0) kwargs.id = svFournisseur
		const skip =
			mountRef.current &&
			stockVirtuel.length > 0 &&
			kwargsMatch(atelierInventaire.lastStockVirtuelKwargs, kwargs)
		if (!skip) {
			dispatch(fetchStockVirtuel(kwargs))
			dispatch(fetchInventaireConsommables())
			if (isChef) dispatch(fetchLiensVirtuels())
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab])

	// Endless scroll du stock virtuel (même principe que les commandes virtuelles).
	useEffect(() => {
		if (tab !== "stockVirtuel") return
		const el = svSentinel
		if (!el || svLoading.loading) return
		const loadNext = () => {
			if (!stockVirtuelHasMore || stockVirtuelLoadingMore) return
			const kwargs = { offset: stockVirtuel.length, limit: SV_LIMIT }
			if (svFournisseur > 0) kwargs.id = svFournisseur
			dispatch(fetchStockVirtuel(kwargs))
		}
		const obs = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) loadNext()
			},
			{ rootMargin: `${SCROLL_MARGIN}px` },
		)
		obs.observe(el)
		// Auto-continue fiable : sentinelle encore visible après un chargement.
		if (isSentinelInView(el)) loadNext()
		return () => obs.disconnect()
	}, [
		tab,
		dispatch,
		svSentinel,
		svLoading.loading,
		stockVirtuel.length,
		stockVirtuelHasMore,
		stockVirtuelLoadingMore,
		svFournisseur,
	])

	// Après une consommation : refetch stock virtuel + fermeture des modales.
	useEffect(() => {
		if (!postConsommation.saved) return
		const t = setTimeout(() => {
			dispatch(postInventaireConsommationReset())
			setConsoTarget(null)
			setCmdPersoId(null)
			const kwargs = { offset: 0, limit: SV_LIMIT }
			if (svFournisseur > 0) kwargs.id = svFournisseur
			dispatch(fetchStockVirtuel(kwargs))
		}, 800)
		return () => clearTimeout(t)
	}, [postConsommation.saved, dispatch, svFournisseur])

	// Après un transfert : refetch stock virtuel + stock physique (crédité) +
	// liens (appris au transfert) + fermeture différée du modal.
	useEffect(() => {
		if (!postTransfert.saved) return
		const t = setTimeout(() => {
			dispatch(postInventaireTransfertReset())
			setTransfertTarget(null)
			const kwargs = { offset: 0, limit: SV_LIMIT }
			if (svFournisseur > 0) kwargs.id = svFournisseur
			dispatch(fetchStockVirtuel(kwargs))
			dispatch(
				fetchInventaireStock(
					categorieSelected > 0 ? categorieSelected : undefined,
				),
			)
			dispatch(fetchInventaireLimitChecker())
			dispatch(fetchLiensVirtuels())
		}, 800)
		return () => clearTimeout(t)
	}, [postTransfert.saved, dispatch, svFournisseur, categorieSelected])

	useEffect(() => {
		mountRef.current = false
	}, [])

	// Après une correction de stock : le SUCCESS a déjà remplacé la liste
	// stock (réponse du POST) — reste le limitChecker, puis fermeture différée.
	useEffect(() => {
		if (!postCorrection.saved) return
		const t = setTimeout(() => {
			dispatch(postInventaireCorrectionReset())
			setCorrectionTarget(null)
			dispatch(fetchInventaireLimitChecker())
		}, 800)
		return () => clearTimeout(t)
	}, [postCorrection.saved, dispatch])

	const usFiltersRef = useRef(urlState.utilisations)

	const getFilters = useCallback(
		(overrideTab, extra) => {
			const t = overrideTab || tab
			const f = {
				stock: {
					categorie: categorieSelected,
					lowOnly,
					aCommanderOnly,
				},
				commandes: { fournisseur: cmdFournisseur, article: cmdArticle },
				stockVirtuel: {
					fournisseur: svFournisseur,
					mois: svMonth,
					annee: svYear,
				},
				commandesVirtuelles: {
					fournisseur: cvFournisseur,
					annulees: cvAnnulees,
				},
				utilisations: usFiltersRef.current,
			}
			return { ...f[t], ...extra }
		},
		[
			tab,
			categorieSelected,
			lowOnly,
			aCommanderOnly,
			cmdFournisseur,
			cmdArticle,
			svFournisseur,
			svMonth,
			svYear,
			cvFournisseur,
			cvAnnulees,
		],
	)

	const changeTab = useCallback(
		(newTab) => {
			setTab(newTab)
			syncUrl(newTab, getFilters(newTab))
		},
		[syncUrl, getFilters],
	)

	const corriger = (payload) =>
		dispatch(
			postInventaireCorrection({
				data: { idArticle: correctionTarget.id, ...payload },
				filters: { categorie: categorieSelected },
			}),
		)

	const changeCategorie = (id) => {
		const value = parseInt(id)
		setCategorieSelected(value)
		dispatch(fetchInventaireStock(value > 0 ? value : undefined))
		syncUrl("stock", { categorie: value, lowOnly, aCommanderOnly })
	}

	const batterieCheck = () => {
		fetch(`${URL}api/inventairecategoriescheck?categorie=batteries`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((res) => res.json())
			.then((json) => batterieCheckPDF(json))
	}

	const changeCmdFiltre = (field, value) => {
		const v = parseInt(value)
		const next = {
			fournisseur: cmdFournisseur,
			article: cmdArticle,
			[field]: v,
		}
		if (field === "fournisseur") setCmdFournisseur(v)
		else setCmdArticle(v)
		const kwargs = { offset: 0, limit: CMD_LIMIT }
		if (next.fournisseur > 0) kwargs.fournisseur = next.fournisseur
		if (next.article > 0) kwargs.article = next.article
		dispatch(fetchInventaireCommandes(kwargs))
		syncUrl("commandes", next)
	}

	const saveCommande = (commande) => {
		dispatch(
			postInventaireCommande(
				JSON.stringify({
					...commande,
					fournisseurSelected: cmdFournisseur,
					articleSelected: cmdArticle,
					recuSelected: 0,
					restantSelected: 0,
				}),
			),
		)
	}

	const rechargerCv = (fournisseur, annulees) => {
		const kwargs = { offset: 0, limit: CV_LIMIT }
		if (fournisseur > 0) kwargs.id = fournisseur
		if (annulees) kwargs.annulees = 1
		dispatch(fetchInventaireVirtuelCommandes(kwargs))
		syncUrl("commandesVirtuelles", { fournisseur, annulees })
	}

	const changeCvFournisseur = (value) => {
		const v = parseInt(value)
		setCvFournisseur(v)
		rechargerCv(v, cvAnnulees)
	}

	const changeCvAnnulees = (v) => {
		setCvAnnulees(v)
		rechargerCv(cvFournisseur, v)
	}

	// GE-79 : après une annulation depuis Stock Virtuel (la commande ou la
	// ligne en sort).
	const rechargerStockVirtuel = () => {
		const kwargs = { offset: 0, limit: SV_LIMIT }
		if (svFournisseur > 0) kwargs.id = svFournisseur
		dispatch(fetchStockVirtuel(kwargs))
	}

	const changeSvFournisseur = (value) => {
		const v = parseInt(value)
		setSvFournisseur(v)
		const kwargs = { offset: 0, limit: SV_LIMIT }
		if (v > 0) kwargs.id = v
		dispatch(fetchStockVirtuel(kwargs))
		syncUrl("stockVirtuel", {
			fournisseur: v,
			mois: svMonth,
			annee: svYear,
		})
	}

	const downloadExcel = () => {
		fetch(`${URL}api/stockvirtuelexcel?month=${svMonth}&year=${svYear}`, {
			headers: { Authorization: `Bearer ${token}` },
		})
			.then((res) => {
				if (res.ok) return res.blob()
				throw new Error("Le téléchargement a échoué.")
			})
			.then((blob) => {
				const url = window.URL.createObjectURL(blob)
				const link = document.createElement("a")
				link.href = url
				link.download = `facturation_${MONTHS_FILE[svMonth]}-${svYear}.xlsx`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				window.URL.revokeObjectURL(url)
			})
			.catch((e) => console.error("Erreur téléchargement:", e))
	}

	const excelDisabled =
		[0, "0"].includes(svYear) || [0, "0"].includes(svMonth)

	const rendre = (idArticleVirtuel, quantite) =>
		dispatch(
			postInventaireConsommation({
				data: { isRendre: true, idArticleVirtuel, quantite },
				filters: { fournisseurId: svFournisseur },
			}),
		)

	const consommer = (idConsommable, idArticleVirtuel, quantite) =>
		dispatch(
			postInventaireConsommation({
				data: { idConsommable, idArticleVirtuel, quantite },
				filters: { fournisseurId: svFournisseur },
			}),
		)

	const consommerCmdPerso = (idEmploye, idCommandeVirtuelle) =>
		dispatch(
			postInventaireConsommation({
				data: { idEmploye, idCommandeVirtuelle },
				filters: { fournisseurId: svFournisseur },
			}),
		)

	const transferer = (idCommandeVirtuelle, lignes) =>
		dispatch(
			postInventaireTransfert({
				data: { idCommandeVirtuelle, lignes },
				filters: { fournisseurId: svFournisseur },
			}),
		)

	// Raccourci depuis la clé d'un article : ouvre le modal de transfert de la
	// commande avec cet article pré-rempli (son dispo) et les autres à 0.
	const transfererDepuisConso = () => {
		const commande = stockVirtuel[consoTarget.ci]
		const cible = commande.articles[consoTarget.ai]
		const initial = {}
		commande.articles.forEach((a) => {
			initial[a.id] =
				a.id === cible.id
					? a.quantite_recue -
						a.quantite_utilisee -
						(a.quantite_reservee || 0)
					: 0
		})
		dispatch(postInventaireConsommationReset())
		setConsoTarget(null)
		setTransfertTarget({ commandeId: commande.id, initial })
	}

	// Article ciblé par la modale de consommation.
	const consoArticle =
		consoTarget &&
		stockVirtuel[consoTarget.ci] &&
		stockVirtuel[consoTarget.ci].articles[consoTarget.ai]

	// Commande ciblée par le modal de transfert.
	const transfertCommande =
		transfertTarget &&
		stockVirtuel.find((c) => c.id === transfertTarget.commandeId)

	const visibleTabs = TABS.filter(
		(t) => (!t.right || hasRight(t.right)) && (!t.boss || isBoss),
	)

	// Onglet Stock : filtres locaux (mutuellement exclusifs).
	const stockLow = stock.filter((a) => limitChecker.includes(a.id))
	const stockACommander = stock.filter((a) => (a.a_commander || 0) > 0)
	const effectiveACommanderOnly = aCommanderOnly && stockACommander.length > 0
	const stockAffiche = lowOnly
		? stockLow
		: effectiveACommanderOnly
			? stockACommander
			: stock

	useEffect(() => {
		if (aCommanderOnly && stockACommander.length === 0) {
			setACommanderOnly(false)
			syncUrl("stock", {
				categorie: categorieSelected,
				lowOnly,
				aCommanderOnly: false,
			})
		}
	}, [aCommanderOnly, stockACommander.length]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{cmdModalOpen && (
				<CommandeModalV2
					fournisseurs={fournisseurs}
					articles={allArticles}
					categories={categories}
					tva={tva}
					postState={postCommande}
					onSave={saveCommande}
					onClose={() => {
						dispatch(postInventaireCommandeReset())
						setCmdModalOpen(false)
					}}
				/>
			)}

			{consoTarget && consoArticle && (
				<ConsoModalV2
					article={consoArticle}
					consommables={consommables}
					postState={postConsommation}
					onRendre={rendre}
					onConsommer={consommer}
					onTransferer={isChef ? transfererDepuisConso : undefined}
					onClose={() => {
						dispatch(postInventaireConsommationReset())
						setConsoTarget(null)
					}}
				/>
			)}

			{transfertTarget && transfertCommande && (
				<TransfertModalV2
					commande={transfertCommande}
					categories={categories}
					allArticles={allArticles}
					liens={liensVirtuels}
					postState={postTransfert}
					initialQuantites={transfertTarget.initial}
					onTransferer={transferer}
					onClose={() => {
						dispatch(postInventaireTransfertReset())
						setTransfertTarget(null)
					}}
				/>
			)}

			{correctionTarget && (
				<CorrectionModalV2
					article={correctionTarget}
					postState={postCorrection}
					onCorriger={corriger}
					onClose={() => {
						dispatch(postInventaireCorrectionReset())
						setCorrectionTarget(null)
					}}
				/>
			)}

			{cmdPersoId !== null && (
				<CmdPersoModalV2
					commande={stockVirtuel.find((c) => c.id === cmdPersoId)}
					employes={employes}
					postState={postConsommation}
					onConsommer={consommerCmdPerso}
					onClose={() => {
						dispatch(postInventaireConsommationReset())
						setCmdPersoId(null)
					}}
				/>
			)}

			<div style={gs.page}>
				<div style={st.content}>
					<div style={st.header}>
						<h2 style={gs.title}>Inventaire</h2>
					</div>

					{/* Onglets */}
					<div style={gs.segmented}>
						{visibleTabs.map((t) => (
							<button
								key={t.key}
								type="button"
								style={gs.segment(tab === t.key)}
								onClick={() => changeTab(t.key)}
							>
								{t.label}
								{t.key === "stock" && stockNonDebite > 0 && (
									<span
										style={{
											marginLeft: 6,
											minWidth: 18,
											height: 18,
											padding: "0 5px",
											borderRadius: 999,
											backgroundColor: COLORS.danger,
											color: "#fff",
											fontSize: 11,
											fontWeight: 700,
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										{stockNonDebite}
									</span>
								)}
							</button>
						))}
					</div>

					{/* Onglet Stock */}
					{tab === "stock" && (
						<>
							<div style={st.toolbar}>
								<div style={st.field}>
									<span style={st.label}>Catégorie</span>
									{fetchCategories.loading ? (
										<div style={st.inlineLoader}>
											<FontAwesomeIcon
												icon={faSync}
												spin
											/>
										</div>
									) : (
										<select
											style={st.input}
											value={categorieSelected}
											onChange={(e) =>
												changeCategorie(e.target.value)
											}
										>
											<option value={0}>Toutes</option>
											{categories.map((c) => (
												<option key={c.id} value={c.id}>
													{c.nom}
												</option>
											))}
										</select>
									)}
								</div>
								<button
									type="button"
									style={{
										...st.btnSecondary,
										...(lowOnly ? st.btnLowActive : {}),
									}}
									onClick={() => {
										const next = !lowOnly
										setLowOnly(next)
										if (next) setACommanderOnly(false)
										syncUrl("stock", {
											categorie: categorieSelected,
											lowOnly: next,
											aCommanderOnly: false,
										})
									}}
									title="N'afficher que les articles sous leur limite"
								>
									Sous la limite ({stockLow.length})
								</button>
								{stockACommander.length > 0 && (
									<button
										type="button"
										style={{
											...st.btnSecondary,
											...(aCommanderOnly
												? st.btnACommanderActive
												: {}),
										}}
										onClick={() => {
											const next = !aCommanderOnly
											setACommanderOnly(next)
											if (next) setLowOnly(false)
											syncUrl("stock", {
												categorie: categorieSelected,
												lowOnly: false,
												aCommanderOnly: next,
											})
										}}
										title="N'afficher que les articles à commander (stock non débité)"
									>
										À commander ({stockACommander.length})
									</button>
								)}
								<button
									type="button"
									style={st.btnSecondary}
									onClick={batterieCheck}
								>
									Check batteries
								</button>
							</div>

							{fetchStock.loading ? (
								<Loader
									variant="ring"
									message="Chargement du stock…"
								/>
							) : (
								<div style={tableStyles.container}>
									<table style={tableStyles.table}>
										<thead>
											<tr>
												<th style={tableStyles.th}>
													Catégorie
												</th>
												<th style={tableStyles.th}>
													Article
												</th>
												<th style={tableStyles.th}>
													Prix de vente
												</th>
												<th style={tableStyles.th}>
													Limite
												</th>
												<th style={tableStyles.th}>
													Quantité (réservé)
												</th>
												{isChef && (
													<th
														style={tableStyles.th}
													/>
												)}
											</tr>
										</thead>
										<tbody>
											{stockAffiche.length === 0 ? (
												<tr>
													<td
														colSpan={isChef ? 6 : 5}
														style={
															tableStyles.empty
														}
													>
														{lowOnly
															? "Aucun article sous la limite"
															: aCommanderOnly
																? "Aucun article à commander"
																: "Aucun article en stock"}
													</td>
												</tr>
											) : (
												stockAffiche.map(
													(article, index) => (
														<StockRow
															key={article.id}
															article={article}
															index={index}
															low={limitChecker.includes(
																article.id,
															)}
															onCorriger={
																isChef
																	? setCorrectionTarget
																	: undefined
															}
														/>
													),
												)
											)}
										</tbody>
									</table>
								</div>
							)}
						</>
					)}

					{/* Onglet Commandes */}
					{tab === "commandes" && (
						<>
							<div style={st.toolbar}>
								<div style={st.field}>
									<span style={st.label}>Fournisseur</span>
									<select
										style={st.input}
										value={cmdFournisseur}
										onChange={(e) =>
											changeCmdFiltre(
												"fournisseur",
												e.target.value,
											)
										}
									>
										<option value={0}>Tous</option>
										{fournisseurs.map((f) => (
											<option key={f.id} value={f.id}>
												{f.nom}
											</option>
										))}
									</select>
								</div>
								<div style={st.field}>
									<span style={st.label}>Article</span>
									<select
										style={st.input}
										value={cmdArticle}
										onChange={(e) =>
											changeCmdFiltre(
												"article",
												e.target.value,
											)
										}
									>
										<option value={0}>Tous</option>
										{allArticles.map((a) => (
											<option key={a.id} value={a.id}>
												{a.nom}
											</option>
										))}
									</select>
								</div>
								<button
									type="button"
									style={st.btnPrimary}
									onClick={() => setCmdModalOpen(true)}
								>
									<FontAwesomeIcon icon={faPlus} /> Nouvelle
									commande
								</button>
							</div>

							{fetchCommandes.loading ? (
								<Loader
									variant="ring"
									message="Chargement des commandes…"
								/>
							) : commandes.length === 0 ? (
								<div style={st.placeholder}>
									Aucune commande existante
								</div>
							) : (
								<>
									{commandes.map((commande) => (
										<CommandeCard
											key={commande.id}
											commande={commande}
										/>
									))}
									{/* Sentinelle endless scroll + état bas de liste */}
									<div
										ref={setCmdSentinel}
										style={st.cvFooter}
									>
										{commandesLoadingMore ? (
											<Loader
												variant="ring"
												size={22}
												padding={0}
											/>
										) : !commandesHasMore ? (
											"Fin de la liste"
										) : null}
									</div>
								</>
							)}
						</>
					)}

					{/* Onglet Stock Virtuel */}
					{tab === "stockVirtuel" && (
						<>
							<div style={st.toolbar}>
								<div style={st.field}>
									<span style={st.label}>Fournisseur</span>
									<select
										style={st.input}
										value={svFournisseur}
										onChange={(e) =>
											changeSvFournisseur(e.target.value)
										}
									>
										<option value={0}>Tous</option>
										{fournisseurs
											.filter((f) =>
												[1, 6, 7].includes(f.id),
											)
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.nom}
												</option>
											))}
									</select>
								</div>
								{/* Mois / Année / Excel poussés à droite */}
								<div style={st.toolbarRight}>
									<div style={st.field}>
										<span style={st.label}>Mois</span>
										<select
											style={st.input}
											value={svMonth}
											onChange={(e) => {
												const v = parseInt(
													e.target.value,
												)
												setSvMonth(v)
												syncUrl("stockVirtuel", {
													fournisseur: svFournisseur,
													mois: v,
													annee: svYear,
												})
											}}
										>
											{MONTHS.map((m, i) => (
												<option key={i} value={i}>
													{m}
												</option>
											))}
										</select>
									</div>
									<div style={st.field}>
										<span style={st.label}>Année</span>
										<select
											style={st.input}
											value={svYear}
											onChange={(e) => {
												const v =
													e.target.value === "-"
														? 0
														: parseInt(
																e.target.value,
															)
												setSvYear(v)
												syncUrl("stockVirtuel", {
													fournisseur: svFournisseur,
													mois: svMonth,
													annee: v,
												})
											}}
										>
											{YEARS.map((y, i) => (
												<option
													key={i}
													value={y === "-" ? 0 : y}
												>
													{y}
												</option>
											))}
										</select>
									</div>
									<button
										type="button"
										style={{
											...st.btnSecondary,
											...(excelDisabled
												? st.btnDisabled
												: {}),
										}}
										disabled={excelDisabled}
										onClick={downloadExcel}
									>
										Excel facturation
									</button>
								</div>
							</div>

							{svLoading.loading ? (
								<Loader
									variant="ring"
									message="Chargement du stock virtuel…"
								/>
							) : stockVirtuel.length === 0 ? (
								<div style={st.placeholder}>
									Aucun stock virtuel restant
								</div>
							) : (
								<>
									{stockVirtuel.map((commande, ci) => (
										<StockVirtuelCard
											key={commande.id}
											commande={commande}
											onConso={(ai) =>
												setConsoTarget({ ci, ai })
											}
											onCmdPerso={() =>
												setCmdPersoId(commande.id)
											}
											onAnnule={rechargerStockVirtuel}
											onTransfert={
												isChef
													? () =>
															setTransfertTarget({
																commandeId:
																	commande.id,
																initial: null,
															})
													: undefined
											}
										/>
									))}
									{/* Sentinelle endless scroll + état bas de liste */}
									<div
										ref={setSvSentinel}
										style={st.cvFooter}
									>
										{stockVirtuelLoadingMore ? (
											<Loader
												variant="ring"
												size={22}
												padding={0}
											/>
										) : !stockVirtuelHasMore ? (
											"Fin de la liste"
										) : null}
									</div>
								</>
							)}
						</>
					)}

					{/* Onglet Commandes Virtuelles */}
					{tab === "commandesVirtuelles" && (
						<>
							<div style={st.toolbar}>
								<div style={st.field}>
									<span style={st.label}>Fournisseur</span>
									<select
										style={st.input}
										value={cvFournisseur}
										onChange={(e) =>
											changeCvFournisseur(e.target.value)
										}
									>
										<option value={0}>Tous</option>
										{fournisseurs
											.filter((f) =>
												[1, 6, 7].includes(f.id),
											)
											.map((f) => (
												<option key={f.id} value={f.id}>
													{f.nom}
												</option>
											))}
									</select>
								</div>
								<div style={st.field}>
									<span style={st.label}>Afficher</span>
									<div style={st.pills}>
										{[
											[false, "Toutes"],
											[true, "Annulées"],
										].map(([v, libelle]) => (
											<button
												key={libelle}
												type="button"
												style={st.pill(
													cvAnnulees === v,
												)}
												onClick={() =>
													cvAnnulees !== v &&
													changeCvAnnulees(v)
												}
											>
												{libelle}
											</button>
										))}
									</div>
								</div>
							</div>

							{fetchCommandesVirtuelles.loading ? (
								<Loader
									variant="ring"
									message="Chargement des commandes virtuelles…"
								/>
							) : commandesVirtuelles.length === 0 ? (
								<div style={st.placeholder}>
									{cvAnnulees
										? "Aucune commande annulée"
										: "Aucune commande virtuelle existante"}
								</div>
							) : (
								<>
									{commandesVirtuelles.map((commande) => (
										<CommandeVirtuelleCard
											key={commande.id}
											commande={commande}
											onModifie={rechargerStockVirtuel}
										/>
									))}
									{/* Sentinelle endless scroll + état bas de liste */}
									<div
										ref={setCvSentinel}
										style={st.cvFooter}
									>
										{commandesVirtuellesLoadingMore ? (
											<Loader
												variant="ring"
												size={22}
												padding={0}
											/>
										) : !commandesVirtuellesHasMore ? (
											"Fin de la liste"
										) : null}
									</div>
								</>
							)}
						</>
					)}

					{/* Onglet Fournisseurs (chef_atelier) */}
					{tab === "fournisseurs" && <FournisseursV2 />}

					{/* Onglet Options : catégories + articles (chef_atelier) */}
					{tab === "options" && <OptionsV2 />}

					{/* Onglet Utilisations : historique (inventaire_utilisations) */}
					{tab === "utilisations" && (
						<UtilisationsV2
							initialCat={urlState.utilisations.cat}
							initialArticle={urlState.utilisations.article}
							skipInitialFetch={
								atelierInventaire.articleUsages.length > 0 &&
								kwargsMatch(
									atelierInventaire.lastArticleUsagesKwargs,
									{
										...(urlState.utilisations.cat > 0
											? {
													categorie:
														urlState.utilisations
															.cat,
												}
											: {}),
										...(urlState.utilisations.article > 0
											? {
													article:
														urlState.utilisations
															.article,
												}
											: {}),
									},
								)
							}
							onFilterChange={(f) => {
								usFiltersRef.current = f
								syncUrl("utilisations", f)
							}}
							onShowCommande={(commande) => {
								const f = commande.fournisseur || 0
								const a = commande.article || 0
								setCmdFournisseur(f)
								setCmdArticle(a)
								setTab("commandes")
								syncUrl("commandes", {
									fournisseur: f,
									article: a,
								})
							}}
						/>
					)}

					{/* Autres onglets — à implémenter */}
					{tab !== "stock" &&
						tab !== "commandes" &&
						tab !== "stockVirtuel" &&
						tab !== "commandesVirtuelles" &&
						tab !== "fournisseurs" &&
						tab !== "options" &&
						tab !== "utilisations" && (
							<div style={st.placeholder}>
								Onglet «{" "}
								{visibleTabs.find((t) => t.key === tab)?.label}{" "}
								» — à venir
							</div>
						)}
				</div>
			</div>
		</>
	)
}

const st = {
	content: { width: "100%", maxWidth: 1320, margin: "0 auto" },
	header: { marginBottom: 24 },
	toolbar: {
		display: "flex",
		alignItems: "flex-end",
		gap: 16,
		marginBottom: 20,
	},
	field: { display: "flex", flexDirection: "column", marginBottom: 14 },
	// GE-79 : filtre secondaire en pills (convention V2).
	pills: { display: "flex", gap: 6, alignItems: "center", minHeight: 38 },
	pill: (active) => ({
		padding: "6px 14px",
		borderRadius: 99,
		border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
		backgroundColor: active ? COLORS.accentLight : COLORS.surface,
		color: active ? COLORS.accent : COLORS.textMuted,
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
	}),
	cvFooter: {
		textAlign: "center",
		padding: "16px",
		color: COLORS.textLight,
		fontSize: 13,
	},
	label: {
		fontSize: 12,
		fontWeight: 600,
		color: COLORS.textMuted,
		marginBottom: 6,
	},
	input: {
		height: 38,
		minWidth: 220,
		padding: "0 12px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		fontSize: 14,
		color: COLORS.text,
		fontFamily: "inherit",
		backgroundColor: COLORS.surface,
		boxSizing: "border-box",
	},
	inlineLoader: {
		height: 38,
		display: "flex",
		alignItems: "center",
		color: COLORS.textLight,
	},
	btnSecondary: {
		height: 38,
		padding: "0 16px",
		borderRadius: 10,
		border: `1px solid ${COLORS.border}`,
		backgroundColor: COLORS.surface,
		color: COLORS.text,
		fontSize: 13,
		fontWeight: 500,
		cursor: "pointer",
		fontFamily: "inherit",
		marginBottom: 14,
	},
	btnDisabled: {
		opacity: 0.5,
		cursor: "not-allowed",
	},
	// Toggle « Sous la limite » actif : reprend le rouge du flag stock bas.
	btnLowActive: {
		borderColor: COLORS.danger,
		color: COLORS.danger,
		fontWeight: 600,
	},
	btnACommanderActive: {
		borderColor: COLORS.warning,
		color: COLORS.warning,
		fontWeight: 600,
	},
	toolbarRight: {
		display: "flex",
		alignItems: "flex-end",
		gap: 16,
		marginLeft: "auto",
		flexWrap: "wrap",
	},
	btnPrimary: {
		display: "inline-flex",
		alignItems: "center",
		gap: 8,
		height: 38,
		padding: "0 18px",
		borderRadius: 10,
		border: "none",
		backgroundColor: COLORS.text,
		color: "#FFFFFF",
		fontSize: 13,
		fontWeight: 600,
		cursor: "pointer",
		fontFamily: "inherit",
		marginBottom: 14,
	},
	placeholder: {
		padding: "48px 24px",
		textAlign: "center",
		color: COLORS.textLight,
		backgroundColor: COLORS.surface,
		borderRadius: 16,
		border: `1px dashed ${COLORS.border}`,
		fontSize: 14,
	},
}

export default InventaireV2
